import { Link } from 'wouter';
import { Brain, ArrowLeft } from 'lucide-react';

const LAST_UPDATED = 'August 13, 2026';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center">
              <Brain className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-lg">MarketMind</span>
          </Link>
          <Link href="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 prose prose-neutral dark:prose-invert prose-headings:font-bold prose-a:text-primary">
        <h1 className="text-3xl font-bold mb-1">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: {LAST_UPDATED}</p>

        <p>
          MarketMind ("we," "our," or "us") provides AI-powered analysis of prediction markets. This
          Privacy Policy explains what information we collect, how we use it, and the choices you
          have. By using MarketMind, you agree to the practices described here.
        </p>

        <h2>1. Information We Collect</h2>
        <ul>
          <li>
            <strong>Account information:</strong> When you sign up, our authentication provider
            (Clerk) collects your name, email address, and sign-in credentials.
          </li>
          <li>
            <strong>Usage data:</strong> Markets you view or watch, analyses you run, chat messages
            you send to our AI features, alerts you configure, and portfolio entries you add.
          </li>
          <li>
            <strong>Billing information:</strong> If you subscribe to MarketMind Pro, payments are
            processed by Stripe. We do not store your full card number; Stripe handles that
            securely on our behalf.
          </li>
          <li>
            <strong>Technical data:</strong> Device/browser type, IP address, and error diagnostics
            captured through our monitoring tools (Sentry) to help us find and fix bugs.
          </li>
          <li>
            <strong>Marketing attribution:</strong> If you arrive via a campaign link, we record
            basic UTM parameters (source, medium, campaign) to understand which channels bring
            people to MarketMind.
          </li>
          <li>
            <strong>Notifications:</strong> If you enable push or email alerts, we store a device
            token or email address so we can deliver them.
          </li>
        </ul>

        <h2>2. How We Use Your Information</h2>
        <ul>
          <li>To provide, maintain, and improve the MarketMind product.</li>
          <li>To generate AI analyses and answer questions you ask about markets.</li>
          <li>To process payments and manage your subscription.</li>
          <li>To send transactional emails and, if enabled, alerts and notifications.</li>
          <li>To detect, investigate, and fix technical problems.</li>
          <li>To understand how people discover and use the product so we can improve it.</li>
        </ul>

        <h2>3. Sharing of Information</h2>
        <p>
          We do not sell your personal information. We share data only with the service providers
          that power MarketMind, including:
        </p>
        <ul>
          <li>Clerk (authentication)</li>
          <li>Stripe (payment processing)</li>
          <li>Kalshi (public prediction market data)</li>
          <li>Resend (transactional email delivery)</li>
          <li>Sentry (error monitoring)</li>
          <li>Our AI model providers (to generate analyses and chat responses)</li>
        </ul>
        <p>
          Each of these providers processes data only as needed to deliver their service to us and
          is bound by their own privacy and security obligations.
        </p>

        <h2>4. Data Retention</h2>
        <p>
          We retain account and usage data for as long as your account is active, plus a reasonable
          period afterward to comply with legal obligations, resolve disputes, and enforce our
          agreements. You can request deletion of your account and associated data at any time.
        </p>

        <h2>5. Your Choices</h2>
        <ul>
          <li>You can update or delete your account information from Settings.</li>
          <li>You can disable email or push notifications at any time from Settings.</li>
          <li>You can request an export or deletion of your data by contacting us.</li>
        </ul>

        <h2>6. Security</h2>
        <p>
          We use industry-standard safeguards, including encrypted connections and access
          controls, to protect your information. No system is completely secure, so we cannot
          guarantee absolute security.
        </p>

        <h2>7. Children's Privacy</h2>
        <p>
          MarketMind is not directed to individuals under 18. We do not knowingly collect
          information from children.
        </p>

        <h2>8. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. If we make material changes, we
          will notify you by updating the "Last updated" date above or through the app.
        </p>

        <h2>9. Contact Us</h2>
        <p>
          If you have questions about this Privacy Policy or how we handle your data, please
          contact us through the support options available in the app.
        </p>
      </main>
    </div>
  );
}
